import type { CallListFilter, CallNumberSaved, CallPop, CallSettings, CallSummary, Person, PhoneExtension, PhoneLine } from '@bre-hub/contracts'
import { qs, request } from './api'

// Call pop (ADR 0038): the staff side of the call log.

export type CallList = {
  calls: CallSummary[]
  total: number
  callbacksOpen: number
  lines: { id: string; label: string; number: string; active: boolean }[]
  peopleHidden: boolean
}

export type CallSettingsView = { lines: PhoneLine[]; extensions: PhoneExtension[]; settings: CallSettings; departments: { id: string; name: string }[] }

const id = (value: string) => encodeURIComponent(value)

export const callsApi = {
  pops: () => request<{ pops: CallPop[] }>('GET', '/api/calls/pops'),
  pop: (callId: string) => request<{ pop: CallPop; recipient: boolean }>('GET', `/api/calls/${id(callId)}/pop`),
  list: (params: { filter: CallListFilter; lineId?: string; q?: string; offset?: number }) =>
    request<CallList>('GET', `/api/calls?${qs({ filter: params.filter, lineId: params.lineId, q: params.q, offset: params.offset ? String(params.offset) : undefined })}`),
  personCalls: (personId: string) => request<{ calls: CallSummary[] }>('GET', `/api/people/${id(personId)}/calls`),
  // rememberNumber: also save the call's number to the person (people.manage).
  confirm: (callId: string, personId: string, rememberNumber: boolean) =>
    request<{ pop: CallPop; numberSaved: CallNumberSaved }>('POST', `/api/calls/${id(callId)}/confirm`, { personId, rememberNumber }),
  createPerson: (callId: string, body: { displayName: string; email?: string }) => request<{ person: Person; pop: CallPop }>('POST', `/api/calls/${id(callId)}/person`, body),
  note: (callId: string, body: string) => request<{ pop: CallPop }>('POST', `/api/calls/${id(callId)}/notes`, { body }),
  dismiss: (callId: string) => request<{ ok: true }>('POST', `/api/calls/${id(callId)}/dismiss`, {}),
  callback: (callId: string, done: boolean) => request<{ call: CallSummary }>('POST', `/api/calls/${id(callId)}/callback`, { done }),
  settings: () => request<CallSettingsView>('GET', '/api/calls/settings'),
  saveSettings: (retentionDays: number | null) => request<{ settings: CallSettings }>('PUT', '/api/calls/settings', { retentionDays }),
  addLine: (body: { number: string; label: string }) => request<{ lines: PhoneLine[] }>('POST', '/api/calls/lines', body),
  updateLine: (lineId: string, body: { label?: string; active?: boolean; version: number }) => request<{ lines: PhoneLine[] }>('PATCH', `/api/calls/lines/${id(lineId)}`, body),
  addAudience: (lineId: string, body: { personId: string } | { departmentId: string }) => request<{ lines: PhoneLine[] }>('POST', `/api/calls/lines/${id(lineId)}/audience`, body),
  removeAudience: (lineId: string, audienceId: string) => request<{ lines: PhoneLine[] }>('POST', `/api/calls/lines/${id(lineId)}/audience/${id(audienceId)}/remove`, {}),
  addExtension: (body: { extension: string; personId: string; label?: string | null }) => request<{ extensions: PhoneExtension[] }>('POST', '/api/calls/extensions', body),
  retireExtension: (extensionId: string) => request<{ extensions: PhoneExtension[] }>('POST', `/api/calls/extensions/${id(extensionId)}/retire`, {}),
}
